import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { MediaURL } from '../../template/imageCarouselTypes';
import AssetImage from '../../AssetImage';
import { heroImageIsSet, heroImageSx, heroImageSxFor } from '../../template/HeroImage';
import { handleImageZonePaste } from './handleImageZonePaste';

interface HeroImageDropZoneProps {
  imageUrl?: MediaURL;
  altText?: string;
  uploading: boolean;
  onFile: (file: File | undefined) => void;
}

const overlaySx = {
  position: 'absolute',
  inset: 0,
  borderRadius: heroImageSx.borderRadius,
  bgcolor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s',
  color: 'white',
  textAlign: 'center',
} as const;

const emptyZoneSx = {
  bgcolor: '#f5f5f5',
  minHeight: 180,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

function ZoneContent({ imageUrl, altText, uploading }: Omit<HeroImageDropZoneProps, 'onFile'>) {
  if (uploading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="caption">Uploading…</Typography>
      </Box>
    );
  }
  if (imageUrl && heroImageIsSet(imageUrl)) {
    return (
      <Box sx={{ position: 'relative', width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
        <AssetImage url={imageUrl.url} alt={altText ?? ''} sx={heroImageSxFor(imageUrl)} />
        <Box className="hero-image-overlay" sx={overlaySx}>
          <Box>
            <UploadIcon sx={{ fontSize: 28, mb: 0.5 }} />
            <Typography variant="caption" display="block">
              Click, drag, or paste to replace
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
      <UploadIcon sx={{ fontSize: 32, mb: 0.5 }} />
      <Typography variant="body2">Click, drag, or paste an image</Typography>
    </Box>
  );
}

/**
 * The hero image shown exactly as investors see it; click, drag or paste an
 * image onto it to set or replace it. An empty block shows a drop hint.
 */
export default function HeroImageDropZone({
  imageUrl,
  altText,
  uploading,
  onFile,
}: HeroImageDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasImage = heroImageIsSet(imageUrl);

  return (
    <>
      <Box
        tabIndex={0}
        role="button"
        aria-label="Hero image — click, drag, or paste to set"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          onFile(event.dataTransfer.files[0]);
        }}
        onClick={() => fileInputRef.current?.click()}
        onPaste={(event) => handleImageZonePaste(event, onFile)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: heroImageSx.borderRadius,
          outline: 'none',
          '&:hover .hero-image-overlay': { opacity: 1 },
          '&:focus-visible': {
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
          },
          ...(hasImage ? {} : emptyZoneSx),
        }}
      >
        <ZoneContent imageUrl={imageUrl} altText={altText} uploading={uploading} />
      </Box>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        aria-label="Choose hero image"
        onChange={(event) => {
          onFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </>
  );
}
